/**
 * AI Bot Framework Types - Self-describing message protocol
 *
 * BotMessage uses self-describing fields instead of messageType enums.
 * Check which fields are populated: content, status, error, interactiveElements, visualization.
 */

import type { InteractiveElement } from './botArtifacts'

// ---------------------------------------------------------------------------
// Sub-types for BotMessage fields
// ---------------------------------------------------------------------------

export interface StatusInfo {
  stage?: string
  message?: string
  tool?: ToolStatus
}

export interface ToolStatus {
  name: string
  phase: 'calling' | 'complete'
  parameters?: Record<string, unknown>
  result?: Record<string, unknown>
}

export interface ErrorInfo {
  code: string
  message: string
  source: string
  isUserFacing: boolean
}

// ---------------------------------------------------------------------------
// Core message type
// ---------------------------------------------------------------------------

export interface BotMessage {
  // Envelope
  sessionId: string
  requestId: string
  botId: string
  botName: string
  timestamp: string

  // Lifecycle
  isComplete: boolean
  isError: boolean

  // Content blocks (render in chat area)
  content?: string
  visualization?: Record<string, unknown>
  interactiveElements?: InteractiveElement[]
  error?: ErrorInfo

  // Status (render in status bar, NOT in chat)
  status?: StatusInfo
}

// ---------------------------------------------------------------------------
// Session management types
// ---------------------------------------------------------------------------

export interface RegisterBotSessionRequest {
  sessionId: string
  requestedBotId: string
  sessionType: SessionType
  pageContext: BotPageContext
  initialMessage?: string
}

export interface SendBotMessageRequest {
  sessionId: string
  content: string
  requestId: string
  metadata?: Record<string, any>
  pageContext?: BotPageContext
}

export interface BotSessionEstablishedResponse {
  sessionId: string
  assignedBot: BotInfo
  requestedBotAvailable: boolean
  fallbackReason?: string
}

export interface BotSwitchedNotification {
  sessionId: string
  previousBot: BotInfo
  newBot: BotInfo
  reason: string
}

export enum SessionType {
  Visible = 'Visible',
  Background = 'Background'
}

export interface BotPageContext {
  page: string
  metadata: Record<string, any>
  url?: string
}

export interface BotInfo {
  botId: string
  botName: string
  description: string
  capabilities: string[]
  version: string
}

// ---------------------------------------------------------------------------
// Hook configuration
// ---------------------------------------------------------------------------

export interface AIBotConfig {
  requestedBotId: string
  pageContext?: BotPageContext
  pageContextRef?: React.MutableRefObject<BotPageContext>
  sessionType: SessionType
  onMessage?: (message: BotMessage) => void
  initialMessage?: string
}

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface AIBotSession {
  sessionId: string
  isConnected: boolean
  isRegistered: boolean
  currentBot: BotInfo | null
  availableBots: BotInfo[]
  messages: BotMessage[]
  isProcessing: boolean
  error: string | null

  // Actions
  sendMessage: (content: string, metadata?: Record<string, any>) => Promise<void>
  respondToElement: (elementId: string, actionMetadata: Record<string, unknown>) => Promise<void>
  switchBot: (newBotId: string) => Promise<void>
  registerSession: () => Promise<void>
  unregisterSession: () => Promise<void>
  clearMessages: () => void

  // Connection management
  reconnect: () => Promise<void>
}

// ---------------------------------------------------------------------------
// SignalR client interface
// ---------------------------------------------------------------------------

export interface AIBotClient {
  ReceiveBotMessage: (message: BotMessage) => void
  ReceiveElementResponse: (message: BotMessage) => void
  BotSessionEstablished: (response: BotSessionEstablishedResponse) => void
  BotSwitched: (notification: BotSwitchedNotification) => void
  SessionTerminated: (sessionId: string, reason: string) => void
  AvailableBotsUpdated: (data: AvailableBotsUpdatedEvent) => void
}

// ---------------------------------------------------------------------------
// Available bots event
// ---------------------------------------------------------------------------

export interface AvailableBotsUpdatedEvent {
  sessionId: string
  availableBots: BotInfo[]
}

// ---------------------------------------------------------------------------
// Chat UI types for components
// ---------------------------------------------------------------------------

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: string
  botName?: string
  isMarkdown?: boolean
  isProcessing?: boolean
  error?: string
}

// ---------------------------------------------------------------------------
// Component configuration
// ---------------------------------------------------------------------------

export interface VisibleChatConfig extends AIBotConfig {
  sessionType: SessionType.Visible
  placeholder?: string
  title?: string
  showBotInfo?: boolean
  maxHeight?: string
  className?: string
}

export interface BackgroundAIConfig extends AIBotConfig {
  sessionType: SessionType.Background
  autoStart?: boolean
}

// ---------------------------------------------------------------------------
// Message interpretation helpers
// ---------------------------------------------------------------------------

export type MessageHandler = (message: BotMessage) => void

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

export interface AIBotError {
  code: string
  message: string
  sessionId?: string
  requestId?: string
  timestamp: string
}

// ---------------------------------------------------------------------------
// Debug information
// ---------------------------------------------------------------------------

export interface SessionDebugInfo {
  sessionId: string
  connectionId?: string
  registeredAt?: string
  lastActivity?: string
  messageCount: number
  requestedBot: string
  assignedBot?: string
  sessionType: SessionType
  pageContext: BotPageContext
}
